import {
  Box,
  Button,
  Flex,
  InputProps,
  List,
  Popover,
  Portal,
  Spinner,
  Text,
  mergeRefs,
} from '@chakra-ui/react';
import { CustomSelectItem, MCustomSelectProps } from '@monetize/types/app';
import { formatInteger } from '@monetize/utils/core';
import clamp from 'lodash/clamp';
import React, {
  ChangeEvent,
  FocusEvent,
  KeyboardEvent,
  useEffect,
  useId,
  useMemo,
  useRef,
} from 'react';
import { MCheckbox } from '..';
import {
  CUSTOM_SELECT_SUBTITLE_TYPE,
  OPTION_TYPE_FIELD,
} from '../../../constants/customSelect';
import useOutsideClick from '../../../hooks/useOutsideClick';
import MText from '../MText';
import { MBox, MDivider, MFlex } from '../chakra';
import MCustomSelectItemListRender from './MCustomSelectItemListRender';
import CustomItemList from './components/CustomItemList';
import { MainInput, MainInputProps } from './components/MainInput';
import SearchInput from './components/SearchInput';
import { useCursor } from './components/useCursor';
import { useQuery } from './components/useQuery';
import { useSelectData } from './components/useSelectData';

/**
 * Index of the first item whose search text starts with `key`, for keyboard
 * type-ahead. Items are not always strings — the page-size select passes raw
 * numbers — so the search value is coerced before comparison.
 */
export const findItemIndexStartingWith = (
  items: any[],
  itemSearch: string,
  key: string,
) => {
  const prefix = key.toLowerCase();
  return items.findIndex((item: any) => {
    const searchValue = typeof item === 'object' ? item?.[itemSearch] : item;
    if (searchValue === null || searchValue === undefined) {
      return false;
    }
    return String(searchValue).toLowerCase().startsWith(prefix);
  });
};

/**
 * Keys that type into the list's search box: printable characters and
 * Backspace. A modifier pressed on its own, such as the Shift that fires
 * before the Tab of Shift+Tab, or a Ctrl/Cmd shortcut leaves focus where it is.
 */
export const isSearchInputKey = ({
  key,
  ctrlKey,
  metaKey,
}: Pick<KeyboardEvent, 'key' | 'ctrlKey' | 'metaKey'>) =>
  !ctrlKey && !metaKey && (key.length === 1 || key === 'Backspace');

/**
 * Keys the focused control handles itself: Enter and Space press a button
 * (Apply, a clear button) and Space toggles a checkbox (Select All). The list's
 * handler would otherwise pick the highlighted option on Enter and send Space
 * to the search box.
 */
export const isNativeControlKey = ({
  key,
  target,
}: Pick<KeyboardEvent, 'key' | 'target'>) => {
  if (target instanceof HTMLButtonElement) {
    return key === 'Enter' || key === ' ';
  }
  return (
    target instanceof HTMLInputElement &&
    target.type === 'checkbox' &&
    key === ' '
  );
};

export const MCustomSelect = React.forwardRef<any, MCustomSelectProps>(
  (
    {
      name,
      children,
      items: externalItems = [],
      /** item property: to be displayed to user */
      itemTitle = 'title',
      /** item property: value to be returned */
      itemValue = 'value',
      itemSearch = itemTitle,
      omitContainsItemSearch = false,
      returnItem = false,
      endpoint = '',
      additionalSearchParams,
      internalFilterFields,
      getByIdEndpointFn,
      getByIdRecordId,
      loadAll = false,
      infiniteScrolling = false,
      isReadOnly = false,
      value,
      /** TODO: Ideally this would be true by default, but causes some tests to fail */
      isLazy = false,
      lazyBehavior = 'unmount',
      onChange,
      onChangeCheckbox,
      popOverProps,
      popOverContentProps = {},
      scrollableRegionProps,
      placeholder,
      loading: externalLoading = false,
      loadingRenderer,
      useExternalQuery = false,
      externalQuery = '',
      onChangeQuery,
      showQueryInput = false,
      renderPreContent = () => null,
      renderItemContent,
      showContentInInput,
      renderContentInInput,
      disabledItems = [],
      inputProps: inputPropsFromParent,
      renderRightElement,
      MainInputComponent,
      useMainInputAsSearchInput,
      checkboxDisplay,
      showCheckCount = false,
      clearAllCheckValues,
      closeButtonText,
      onCloseButtonClick,
      isCloseButtonLoading = undefined,
      closeOnBlur = true,
      externalOnClose,
      prependItems,
      prependItemsOutsideScrollArea,
      clearable = false,
      onClear,
      onMultiSelectSubmit,
      onOpenStateChange,
      isSelectOpen = false,
      /** this is a bit of hack because this boolean must be set to true if this component is passed a value for renderItemContent that is rendering a UserAvatar along with the user name */
      displayAvatar = false,
      renderInputLeftElement = false,
      inputLeftElementContent,
      isDisabled = false,
      usePortal = false,
      transformDataFromApi,
      filterItems,
      useHighlightedItemList = false,
      hideHighlightSearchResultsCount = false,
      useRawValue,
      showItemDivider = false,
      skipFilter = false,
      onFetchedItems,
      isTruncatedInputTitle = false,
      onNextCall,
      hasMoreDataToCall = false,
      showSelectAll = false,
      onSelectAll,
      rightLabel,
      omitNoResultsText,
      inputTestId,
      ...rest
    }: MCustomSelectProps,
    ref,
  ) => {
    // Use React.useId instead (available in React 18+)
    const containerId = useId();
    // Derive from the per-instance id, not `name` — several selects can share a
    // `name` on one screen, which would otherwise collide the listbox id and
    // mis-target aria-controls.
    const listId = `${containerId}-list`;
    // Spread last onto the input, so they override its combined `disabled`
    // flag (which also covers read-only) the way v2's `isDisabled`/`isReadOnly`
    // did: a read-only select stays focusable, just not editable. Native
    // names, so nothing leaks to the DOM as an unknown prop.
    const inputProps: InputProps = {
      ...inputPropsFromParent,
      disabled: isDisabled,
      readOnly: isReadOnly,
    };
    const { query, setQuery } = useQuery({
      useExternalQuery,
      externalQuery,
      onChangeQuery,
    });
    const internalDisabledItems = disabledItems || [];

    const isDisabledItem = (item: any) => {
      const isObject = typeof item === 'object';
      if (isSubtitleItem(item)) {
        return true;
      }
      if (returnItem) {
        const foundDisabledItem = internalDisabledItems.find((dI) => {
          return (
            dI[itemValue as string] ===
            (isObject ? item[itemValue as string] : item)
          );
        });
        return !!foundDisabledItem;
      }
      const foundDisabledItem = internalDisabledItems.find((dI) => {
        return dI === (isObject ? item[itemValue as string] : item);
      });
      return !!foundDisabledItem;
    };

    const isSelectedItem = (item: any) => {
      const val = getValue(item);
      /**
       * if there is no value then we should return false
       * Since undefined === undefined is true in js
       */
      if (!val) {
        return false;
      }

      if (returnItem) {
        if (Array.isArray(value)) {
          const foundItem = value.find((currValue) => {
            return currValue[itemValue as string] === val;
          });
          return !!foundItem;
        }
        return value && value[itemValue as string] === val;
      } else {
        if (Array.isArray(value)) {
          const foundItem = value.find((currValue) => {
            return currValue === val;
          });
          return !!foundItem;
        }
        return value === val;
      }
    };

    const isSubtitleItem = (item: any) =>
      typeof item === 'object'
        ? item[OPTION_TYPE_FIELD] === CUSTOM_SELECT_SUBTITLE_TYPE
        : false;

    const {
      getValue,
      items,
      getTitle,
      getTitleFromValue,
      hasMore,
      totalRecordCount,
      loading,
      fetchNextPage,
      isFetchingNextPage,
      isInfiniteScrolling,
    } = useSelectData({
      endpoint: endpoint!,
      additionalSearchParams,
      getByIdEndpointFn,
      getByIdRecordId: getByIdRecordId || value,
      isDisabled: isReadOnly || isDisabled,
      internalFilterFields,
      loadAll,
      infiniteScrolling,
      itemValue,
      externalItems,
      itemTitle,
      query,
      itemSearch,
      omitContainsItemSearch,
      isSubtitleItem,
      returnItem,
      transformDataFromApi,
      filterItems,
      useRawValue,
      skipFilter,
      onFetchedItems,
    });

    const [isOpen, setIsOpen] = React.useState<boolean>(isSelectOpen || false);
    const applyPendingRef = useRef(false);
    const [isAllSelected, setIsAllSelected] = React.useState(false);

    const itemsWithoutSubtitle = useMemo(() => {
      return items.filter((item) => !isSubtitleItem(item));
    }, [items]);

    const onOpen = () => {
      // A read-only input is focusable, so ArrowDown can reach here.
      if (isDisabled || isReadOnly) {
        return;
      }
      setIsOpen(true);
    };
    const onClose = (skipInputFocus?: boolean) => {
      if (!isOpen) {
        return;
      }
      setCursor(-1);
      setQuery('');
      setIsOpen(false);
      externalOnClose && externalOnClose();
      if (!(typeof skipInputFocus === 'boolean' && skipInputFocus)) {
        mainInputRef?.current?.focus();
      }
    };
    const searchResultRef = React.useRef<any>(undefined);
    const mainInputRef = React.useRef<any>(undefined);
    const popoverContentRef = useRef<HTMLDivElement>(null);
    const resultListRef = React.useRef<any>(undefined);

    const isItemSelectable = (item: any) => {
      if (typeof item === 'object') {
        if (item[OPTION_TYPE_FIELD] === CUSTOM_SELECT_SUBTITLE_TYPE) {
          return false;
        } else if (item.item && item.isHeading) {
          // is prepended items and is considered heading
          return false;
        }
      }
      return true;
    };

    const itemRefs = useRef<{ [index: number]: HTMLDivElement }>({} as any);

    const {
      cursor,
      setCursor,
      setCursorCurrent,
      setNextCursor,
      setPrevCursor,
      setCursorAndScroll,
    } = useCursor({
      isItemSelectable,
      items,
      prependItems,
      resultListRef,
      isDisabledItem,
      itemRefs,
    });

    const handleChange = (item: any, event?: any) => {
      if (isSubtitleItem(item)) {
        return;
      }
      const returnValue = returnItem ? item : getValue(item);
      if (onChange) {
        onChange(returnValue);
      }
      onChangeCheckbox && onChangeCheckbox(returnValue, event);
      if (!closeButtonText && !checkboxDisplay) {
        onClose();
      }
    };

    const onQueryInputChange = (queryString: string) => {
      setQuery(queryString);
      setCursor(-1);
    };

    const onKeyDown = (e: any) => {
      if (e.key === 'Enter' && !isOpen) {
        return null;
      }
      if ((e.shiftKey && e.key === 'Tab') || e.key === 'Tab') {
        return null;
      }
      e.stopPropagation();
      if (isNativeControlKey(e)) {
        return null;
      }
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (!isOpen) {
            onOpen();
            setCursor(0);
          } else {
            isOpen && setNextCursor();
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          isOpen && setPrevCursor();
          break;
        case 'Escape':
          onClose();
          setCursor(-1);
          e.preventDefault();
          onClose();
          mainInputRef?.current?.focus();
          break;
        case 'Enter': {
          e.preventDefault();
          e.stopPropagation();
          // Combine pre-pended items and regular items and wrap in structure to detect type
          const currItems = [
            ...(prependItems || []).map(
              (item): { type: 'prependedItem'; item: CustomSelectItem } => ({
                type: 'prependedItem',
                item,
              }),
            ),
            ...(items || []).map((item): { type: 'normalItem'; item: any } => ({
              type: 'normalItem',
              item,
            })),
          ];
          // blur the main input when item is selected via keyboard
          // mainInputRef?.current?.blur();
          !checkboxDisplay && mainInputRef?.current?.focus();
          // Default cursor, and ensure it is never out of range
          let selectCursor = clamp(
            cursor >= 0 ? cursor : 0,
            0,
            currItems.length,
          );
          if (!!query && cursor < 0) {
            // when query is present(when search is active) and cursor is -1, select first item if one is visible
            selectCursor = clamp(
              prependItems?.length || 0,
              0,
              currItems.length,
            );
          }
          if (currItems.length > 0 && currItems[selectCursor]) {
            const { type, item } = currItems[selectCursor];
            if (type === 'normalItem') {
              if (checkboxDisplay) {
                itemRefs.current[selectCursor].click();
              } else {
                handleChange(item);
              }
            } else {
              item.onClick?.({ onClose });
            }
          }
          break;
        }
        default:
          if (showQueryInput) {
            if (isSearchInputKey(e)) {
              searchResultRef?.current?.focus();
              setCursor(-1);
            }
          } else {
            const index = findItemIndexStartingWith(items, itemSearch!, e.key);
            if (index !== -1) {
              setCursorAndScroll(index);
            }
          }
          break;
      }
    };

    const onToggleOpen = () => {
      if (!isDisabled && !isReadOnly) {
        setIsOpen(!isOpen);
      }
    };

    const handleRenderInputLeftElement = () => {
      if (renderInputLeftElement) {
        return inputLeftElementContent;
      }
      return;
    };

    const renderPopoverTriggerInternal = () => {
      const mainInputProps: Omit<MainInputProps, 'ref'> = {
        getTitleFromValue,
        disabled: isDisabled || isReadOnly,
        isReadOnly,
        onOpen,
        onClose,
        loading,
        value,
        onToggleOpen,
        externalLoading,
        placeholder,
        isOpen,
        items,
        cursor,
        setCursor,
        handleChange,
        showContentInInput,
        renderContentInInput,
        inputProps,
        renderRightElement,
        clearable,
        onClear,
        onChange,

        queryResultCount: 4,
        query,
        onQueryKeyDown: onKeyDown,
        onQueryChange: onQueryInputChange,

        loadingRenderer,

        displayAvatar,
        renderInputLeftElement,
        inputLeftElementContent: handleRenderInputLeftElement(),
        isSubtitleItem,
        isTruncatedInputTitle,
        showQueryInput,
        useMainInputAsSearchInput,
        containerId,
      };

      if (MainInputComponent) {
        return <MainInputComponent {...mainInputProps} ref={mainInputRef} />;
      }
      return <MainInput {...mainInputProps} ref={mainInputRef} />;
    };

    useEffect(() => {
      onOpenStateChange && onOpenStateChange(isOpen);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // Focus search input when opened
    // added small timeout to ensure element is rendered to allow focus
    useEffect(() => {
      const foundFirstSelectedItemIndex = items.findIndex(isSelectedItem);
      if (foundFirstSelectedItemIndex !== -1) {
        setCursorAndScroll(
          (prependItems?.length || 0) + foundFirstSelectedItemIndex,
        );
      } else if (isOpen && showQueryInput) {
        const focusTimeout = setTimeout(() => {
          // Only while focus hasn't moved on since opening. A user already on
          // Apply, an option, or outside the select was pulled back to the
          // search box, so their Enter or Shift+Tab went astray.
          const { activeElement } = document;
          if (
            !activeElement ||
            activeElement === document.body ||
            activeElement === mainInputRef.current ||
            activeElement === popoverContentRef.current
          ) {
            searchResultRef?.current?.focus();
          }
        }, 50);
        return () => clearTimeout(focusTimeout);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, showQueryInput, items]);

    useEffect(() => {
      if (showSelectAll) {
        setIsAllSelected(itemsWithoutSubtitle?.length === value?.length);
      }
    }, [value, itemsWithoutSubtitle, showSelectAll]);

    useEffect(() => {
      if (applyPendingRef.current && !isCloseButtonLoading) {
        applyPendingRef.current = false;
        onClose();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCloseButtonLoading]);

    const PopoverContentWrapper = usePortal ? Portal : React.Fragment;

    const handleScroll = () => {
      if (!resultListRef.current) {
        return;
      }
      const { scrollTop, clientHeight, scrollHeight } = resultListRef.current;
      const reachedBottom = scrollTop + clientHeight >= scrollHeight - 48;
      if (!reachedBottom) {
        return;
      }
      if (isInfiniteScrolling) {
        if (hasMore && !isFetchingNextPage) {
          fetchNextPage();
        }
        return;
      }
      if (hasMoreDataToCall) {
        onNextCall?.();
      }
    };

    const selectedItemsCount = useMemo(
      () => (showSelectAll ? (value?.length ?? 0) : 0),
      [showSelectAll, value],
    );

    const handleSelectAll = (event: ChangeEvent<HTMLInputElement>) => {
      const isChecked = event.target.checked;
      setIsAllSelected(isChecked);

      const filteredItems = items.filter((item) => !isSubtitleItem(item));
      onSelectAll?.(filteredItems, event);
    };

    // since onClose{} was competing with other open/close logic. we need to handle outside click ourselves
    const containerRef = useRef<HTMLDivElement>(null);
    // The popover is positioned against this box, which wraps just the input.
    const anchorRef = useRef<HTMLDivElement>(null);
    const anchorRefs = useMemo(() => mergeRefs(ref, anchorRef), [ref]);

    useOutsideClick({
      ref: containerRef,
      handler: ({ target }) =>
        isOpen &&
        !popoverContentRef.current?.contains(target as Node) &&
        onClose(),
    });

    const handleContainerBlur = (ev: FocusEvent<HTMLDivElement>) => {
      if (!isOpen || !closeOnBlur) {
        return false;
      }
      // user navigated off the component
      if (
        !ev.relatedTarget ||
        (!containerRef?.current?.contains(ev.relatedTarget) &&
          !popoverContentRef?.current?.contains(ev.relatedTarget))
      ) {
        onClose(true);
      }
    };

    // The wrapper only exists for DOM containment (outside-click / blur), so it
    // renders no box of its own: the inner Box is the real flex/grid item.
    // That lets caller sizing (`width`, `maxW`, `flex` via `rest`) size the
    // select in a row, while the inner `w="100%"` still fills a vertical
    // `Field.Root`, which no longer stretches its children in v3.
    return (
      <MBox display="contents" ref={containerRef} onBlur={handleContainerBlur}>
        <Box
          position="relative"
          w="100%"
          ref={anchorRefs}
          {...rest}
          onKeyDown={onKeyDown}
          data-testid={inputTestId ?? `customSelect-${name}`}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={listId}
          role="combobox"
        >
          {renderPopoverTriggerInternal()}
          <Popover.Root
            open={isReadOnly ? undefined : isOpen}
            lazyMount={isLazy}
            unmountOnExit={lazyBehavior === 'unmount'}
            closeOnInteractOutside={
              useMainInputAsSearchInput ? false : closeOnBlur
            }
            autoFocus={useMainInputAsSearchInput ? false : true}
            // Open into the search box. Left to itself zag focuses the first
            // tabbable element, which is the Select All checkbox when shown;
            // the effect above only focuses the search when nothing is
            // selected.
            initialFocusEl={
              showQueryInput ? () => searchResultRef.current : undefined
            }
            // Only turns off zag's Tab proxying between trigger and content.
            // The trigger here is an unfocusable box, so proxying would
            // swallow Shift+Tab out of the list; the select handles Tab itself.
            portalled={false}
            {...popOverProps}
            // The content element is the listbox `aria-controls` points at.
            // Give zag the id instead of setting `id` on `Popover.Content`:
            // that replaced zag's own content id, so the popover never found
            // its content and never registered as a dismissable layer (Escape
            // then reached an enclosing dialog and closed it too).
            ids={{ ...popOverProps?.ids, content: listId }}
            onOpenChange={(e) => {
              if (e.open) {
                onOpen();
              }
            }}
            positioning={{
              placement: 'bottom-start',
              sameWidth: true,
              strategy: 'fixed',
              // Sit flush against the input, as in v2. The trigger is a
              // zero-height box below the input, so anchoring on it with the
              // default 8px gutter left a gap underneath and, once flipped
              // above, covered most of the input.
              getAnchorElement: () => anchorRef.current,
              gutter: 0,
              ...popOverProps?.positioning,
            }}
          >
            <Popover.Trigger asChild>
              <Box />
            </Popover.Trigger>
            <PopoverContentWrapper>
              <Popover.Positioner>
                <Popover.Content
                  ref={popoverContentRef}
                  data-content-id={containerId}
                  /**
                   * Border, border-radius, and shadow inherit from the Popover
                   * theme (Chakra component theme: 8px radius, border.subtle,
                   * shadow.md). This keeps MCustomSelect visually consistent
                   * with menus, plain popovers, and the rest of V2.
                   *
                   * Width: matches input as minimum, can grow with content but
                   * won't extend past the viewport.
                   */
                  w="fit-content"
                  minW="100%"
                  maxW="95vw"
                  mt="0"
                  whiteSpace="nowrap"
                  // The popover is portaled (usePortal is on) so keydown events
                  // inside the popover don't bubble to the outer wrapper's
                  // onKeyDown. Wire the same handler here so Escape / Arrow keys
                  // work when focus is on a menu item rather than the search input.
                  onKeyDown={onKeyDown}
                  {...popOverContentProps}
                  role="listbox"
                >
                  {showSelectAll && (
                    <MBox px={3} pt={4}>
                      <MCheckbox
                        // Own id so it doesn't take an enclosing Field's
                        // control id (see CheckboxItemList).
                        id={`${containerId}-select-all`}
                        isChecked={isAllSelected}
                        isIndeterminate={
                          !isAllSelected && selectedItemsCount > 0
                        }
                        mb={4}
                        w="100%"
                        onChange={handleSelectAll}
                      >
                        Select All ({selectedItemsCount})
                      </MCheckbox>
                      <MDivider />
                    </MBox>
                  )}

                  {checkboxDisplay && showCheckCount && !!value.length && (
                    <Box px={2} pt={2}>
                      <Flex
                        p={2}
                        justifyContent={'space-between'}
                        alignItems={'center'}
                      >
                        <Text fontSize={'xs'} color={'tPurple.base'}>
                          ({value.length} Selected)
                        </Text>
                        <Text
                          fontSize={'x-small'}
                          color={'tIndigo.base'}
                          _hover={{ cursor: 'pointer' }}
                          onClick={() =>
                            clearAllCheckValues && clearAllCheckValues()
                          }
                        >
                          Clear All
                        </Text>
                      </Flex>
                    </Box>
                  )}
                  {renderPreContent && renderPreContent()}
                  {showQueryInput && (
                    <SearchInput
                      ref={searchResultRef}
                      value={query}
                      onKeyDown={onKeyDown}
                      onChange={onQueryInputChange}
                    />
                  )}

                  {prependItemsOutsideScrollArea && !!prependItems?.length && (
                    <List.Root variant="plain">
                      <CustomItemList
                        customItems={prependItems}
                        cursor={cursor}
                        isDisabledItem={isDisabledItem}
                        isSubtitleItem={isSubtitleItem}
                        onClose={onClose}
                        itemRefs={itemRefs}
                        getTitle={getTitle}
                      />
                      {!checkboxDisplay && <Box h="2" />}
                    </List.Root>
                  )}

                  <List.Root
                    // v3's default `marker` variant adds bullets; v2's
                    // `List` had none.
                    variant="plain"
                    onScroll={handleScroll}
                    ref={resultListRef}
                    maxH="min(calc(100vh / 4), 400px)"
                    overflow="auto"
                    className="custom-scroll-bar-v1"
                    p={
                      (!prependItemsOutsideScrollArea &&
                        prependItems &&
                        prependItems?.length > 0) ||
                      items.length > 0
                        ? '2'
                        : 0
                    }
                    {...scrollableRegionProps}
                  >
                    {!prependItemsOutsideScrollArea &&
                      !!prependItems?.length && (
                        <>
                          <CustomItemList
                            customItems={prependItems}
                            cursor={cursor}
                            isDisabledItem={isDisabledItem}
                            isSubtitleItem={isSubtitleItem}
                            onClose={onClose}
                            itemRefs={itemRefs}
                            getTitle={getTitle}
                          />
                          {!checkboxDisplay && <Box h="2" />}
                        </>
                      )}
                    <MCustomSelectItemListRender
                      checkboxDisplay={checkboxDisplay}
                      cursor={cursor}
                      displayAvatar={displayAvatar}
                      getTitle={getTitle}
                      handleChange={handleChange}
                      indexOffset={prependItems?.length || 0}
                      isDisabledItem={isDisabledItem}
                      isSelectedItem={isSelectedItem}
                      isSubtitleItem={isSubtitleItem}
                      itemRefs={itemRefs}
                      items={items}
                      onClose={onClose}
                      query={query}
                      renderItemContent={renderItemContent}
                      setCursor={setCursor}
                      setCursorCurrent={setCursorCurrent}
                      useHighlightedItemList={useHighlightedItemList}
                      showItemDivider={showItemDivider}
                      rightLabel={rightLabel}
                      omitNoResultsText={omitNoResultsText}
                    />
                    {isFetchingNextPage && (
                      <Box as="li" listStyleType="none">
                        <Flex justify="center" align="center" py={2}>
                          <Spinner size="xs" />
                        </Flex>
                      </Box>
                    )}
                  </List.Root>

                  {items.length > 0 && closeButtonText && (
                    <Box p={2}>
                      <Button
                        variant="primary"
                        width="100%"
                        loading={isCloseButtonLoading}
                        disabled={isCloseButtonLoading}
                        onClick={() => {
                          onMultiSelectSubmit?.();
                          if (
                            isCloseButtonLoading !== undefined ||
                            onCloseButtonClick
                          ) {
                            applyPendingRef.current = true;
                            onCloseButtonClick?.();
                          } else {
                            onClose();
                          }
                        }}
                        data-testid="customSelect-action-btn"
                        mt="0.5"
                      >
                        {closeButtonText}
                      </Button>
                    </Box>
                  )}
                  {useHighlightedItemList &&
                    items.length > 0 &&
                    !hideHighlightSearchResultsCount && (
                      <>
                        <MDivider />
                        <MFlex
                          color="tGray.darkPurple"
                          p={2}
                          justify="center"
                          align="center"
                        >
                          {hasMore && (
                            <MText>
                              Showing {formatInteger(items.length)} of{' '}
                              {formatInteger(totalRecordCount)} results
                            </MText>
                          )}
                          {!hasMore && (
                            <MText>
                              Showing {formatInteger(items.length)} results
                            </MText>
                          )}
                        </MFlex>
                      </>
                    )}
                </Popover.Content>
              </Popover.Positioner>
            </PopoverContentWrapper>
          </Popover.Root>
        </Box>
      </MBox>
    );
  },
);
